<template>
  <!-- 予定表の発行ボタン -->
  <div v-if="billing">
    <div class="button-area">
      <table class="table table-bordered">
        <thead>
          <tr class="table-info">
            <th class="text-center">作成日時</th>
            <th class="text-center">更新日時</th>
            <th class="text-center">PDF出力日時</th>
            <th class="text-center">納品書の有無</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td class="text-center">{{ formatFullDateTime(billing.created_at) }}</td>
            <td class="text-center">{{ formatFullDateTime(billing.updated_at) }}</td>
            <td class="text-center">{{ formatFullDateTime(billing.pdf_latest_output_datetime) }}</td>
            <td class="text-center">{{ deliveryNote ? 'あり' : 'なし' }}</td>
          </tr>
        </tbody>
      </table>
    </div>

    <div class="button-area">
      <!-- 請求書一覧ボタン -->
      <router-link :to="{ name: 'BillingList', query: { ...route.query, year, month } }">
        <button type="button" class="btn btn-primary">請求書一覧</button>
      </router-link>
      <!-- 編集ボタン -->
      <router-link
        v-if="billing && billing.id && !isNaN(parseInt(billing.id)) && (billing.status === 1 || billing.status === 2 || billing.status === 3)"
        :to="{ 
          name: 'BillingEdit',
          params: { id: parseInt(billing.id), year: year, month: month },
          query: { ...route.query, year: year, month: month, from: 'detail' }
        }"
      >
        <button type="button" class="btn btn-warning">請求書編集</button>
      </router-link>

      <button class="btn btn-info" @click="generatePDF">PDFを出力</button>
      
      <!-- メールで送信ボタン -->
      <button type="button" class="btn btn-success" @click="navigateToEmailScreen" disabled="disabled">
        メールで送信
      </button>
    </div>

    <BillingDetailJa v-if="lang === 'ja'" :billing="billing" />
    <BillingDetailEn v-if="lang === 'en'" :billing="billing" />
  </div>

</template>

<script setup>
import { createApp, ref, nextTick, onMounted, watchEffect } from 'vue';
import html2pdf from 'html2pdf.js'
import { useRouter } from 'vue-router';
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';
import BillingDetailJaPDF from './BillingDetailJaPDF.vue';
import BillingDetailEnPDF from './BillingDetailEnPDF.vue';
import BillingDetailWithDeliveryPDF from './BillingDetailWithDeliveryPDF.vue';
import {
  getMonthlyBilling,
  updateBuildingMonthlyWorkBillingPdfStatus

} from '@/api/axios';
import BillingDetailJa from './BillingDetailJa.vue';
import BillingDetailEn from './BillingDetailEn.vue';

const lang = ref('ja');
const deliveryNote = ref(false);
const customer = ref({});
const billing = ref(null);
const generatedPdfBase64 = ref('');
const router = useRouter();
const route = useRoute();
const store = useStore();
const billingId = route.params.id;
const year = route.query.year || new Date().getFullYear();
const month = route.query.month || new Date().getMonth() + 1;
const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
const pdfPagebreakOptions = {
  mode: ['css', 'legacy'],
  avoid: ['.billing-detail-row', '.billing-summary-row']
};

const waitForPdfComponentReady = async (container, fallbackWaitMs) => {
  const getReadyElements = () => Array.from(container.querySelectorAll('[data-pdf-ready]'));
  const allReady = () => {
    const readyElements = getReadyElements();
    return readyElements.length > 0 && readyElements.every((element) => element.dataset.pdfReady === 'true');
  };

  if (getReadyElements().length === 0) {
    await sleep(fallbackWaitMs);
    return true;
  }

  if (allReady()) return true;

  return new Promise((resolve) => {
    let observer = null;
    const timeoutId = setTimeout(() => {
      if (observer) observer.disconnect();
      resolve(false);
    }, 15000);

    const finish = () => {
      clearTimeout(timeoutId);
      if (observer) observer.disconnect();
      resolve(true);
    };

    observer = new MutationObserver(() => {
      if (allReady()) finish();
    });

    observer.observe(container, {
      attributes: true,
      childList: true,
      subtree: true,
      attributeFilter: ['data-pdf-ready']
    });

    if (allReady()) finish();
  });
};

const readBlobAsBase64 = (blob) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const result = typeof reader.result === 'string' ? reader.result : '';
      resolve(result.split(',')[1] || '');
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
};

watchEffect(() => {
  document.title = '請求書詳細';
});

const fetchBillingDetail = async () => {
  try {
    const response = await getMonthlyBilling(billingId);
    billing.value = response.data.monthly_billing;
    customer.value = billing.value?.m_customer || {};
    billing.value.customer_address = format_address(customer.value) || '';
    lang.value = billing.value.billing_lang_type === 1 ? 'en' : 'ja';
    deliveryNote.value = billing.value.delivery_note_flg;
  } catch (error) {
    console.error('請求書情報の取得に失敗しました:', error);
  }
};

// 年月日時分秒
const formatFullDateTime = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);

  const year = date.getFullYear();
  const month = date.getMonth() + 1; // 月は0始まり
  const day = date.getDate();
  const hours = date.getHours();
  const minutes = date.getMinutes();
  const seconds = date.getSeconds();

  return `${year}年${month}月${day}日 ${hours}時${minutes}分${seconds}秒`;
};

// 顧客情報から住所をフォーマットする関数（英語）
const format_address = (customer) => {
  if (!customer) return '';
  const line1 = [customer.other_address, customer.city].filter(Boolean).join(', ');
  const line2 = [customer.prefecture, customer.postal_code].filter(Boolean).join(', ');
  return `${line1}\n${line2}`;
};

const buildPdfFilename = () => {
  const documentName = deliveryNote.value ? '請求書_納品書' : '請求書';
  return `${year}年${month}月度_${billing.value.m_customer.customer_nm}${billing.value.honorific_title}_${documentName}.pdf`;
};

const createBillingPdfApp = () => {
  if (deliveryNote.value === true) {
    return createApp(BillingDetailWithDeliveryPDF, {
      billingId,
      lang: lang.value
    });
  }

  return lang.value === 'ja'
    ? createApp(BillingDetailJaPDF, { billingId })
    : createApp(BillingDetailEnPDF, { billingId });
};

const createPdfOptions = (filename, margin = [0, 4]) => ({
  margin,
  filename,
  image: { type: 'jpeg', quality: 0.7 },
  html2canvas: { scale: 1.5 },
  pagebreak: pdfPagebreakOptions,
  jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
});

const generatePDF = async () => {
  store.dispatch('startGlobalLoading');
  let el = null;
  let app = null;

  try {
    // ※ DOMコンテナ作成
    el = document.createElement('div');
    document.body.appendChild(el);

    // コンポーネントをマウント
    app = createBillingPdfApp();
    app.mount(el);

    await nextTick();

    const pdfReady = await waitForPdfComponentReady(el, 700);
    if (!pdfReady) throw new Error('PDF出力用コンポーネントの準備が完了しませんでした。');

    // PDF生成
    await html2pdf().set(createPdfOptions(buildPdfFilename())).from(el).save();

    // PDF出力完了後、Railsに通知
    await updateBuildingMonthlyWorkBillingPdfStatus(billingId);

    // 全PDF生成完了後に請求データ再取得して画面更新
    const response = await getMonthlyBilling(billingId);
    billing.value = response.data.monthly_billing;
    customer.value = billing.value?.m_customer || {};
    billing.value.customer_address = format_address(customer.value) || '';
    lang.value = billing.value.billing_lang_type === 1 ? 'en' : 'ja';

  } catch (error) {
    console.error('PDF生成エラー:', error);
  } finally {
    // クリーンアップ
    if (app) app.unmount();
    if (el?.parentNode) el.parentNode.removeChild(el);
    store.dispatch('stopGlobalLoading');
  }
};

// メール送信時のPDF作成
const generateAndAttachPDF = async () => {
  store.dispatch('startGlobalLoading');
  let el = null;
  let app = null;

  try {
    el = document.createElement('div');
    document.body.appendChild(el);

    app = createBillingPdfApp();

    app.mount(el);

    await nextTick();
    const pdfReady = await waitForPdfComponentReady(el, 500);
    if (!pdfReady) throw new Error('PDF出力用コンポーネントの準備が完了しませんでした。');

    const pdfBlob = await html2pdf().set(createPdfOptions(buildPdfFilename(), [0, 10])).from(el).outputPdf('blob');
    generatedPdfBase64.value = await readBlobAsBase64(pdfBlob);
  } catch (error) {
    console.error('❌ PDF生成エラー:', error);
    throw error;
  } finally {
    if (app) app.unmount();
    if (el?.parentNode) el.parentNode.removeChild(el);
    store.dispatch('stopGlobalLoading');
  }
};

// PDFが作成されるのを待ってからメール送信画面へ遷移する
const navigateToEmailScreen = async () => {
  try {
    await generateAndAttachPDF();  // ✅ PDF生成完了まで待つ

    sessionStorage.setItem("pdfData", generatedPdfBase64.value);  // ✅ sessionStorage に保存

    router.push({ 
      name: 'BuildingWorkMonthlyBillingEmailCreate',
      params: { id: parseInt(billing.value.id), year: year, month: month },
      query: { year, month }  // ❌ `pdfData` を query に入れない
    });
  } catch (error) {
    console.error("❌ PDF 生成中にエラーが発生:", error);
  }
};

onMounted(fetchBillingDetail);
</script>

<style scoped>
.button-area{
  width: 70%;
  margin: 20px auto;
}

.button-area {
  text-align: right;
  margin-bottom: 20px;
}

.button-area .btn {
  margin-left: 10px;
}
</style>
